<script setup lang="ts">
import { shallowRef } from "vue"

import type { RejectedCapturePhoto } from "@/composables/useBulkCapture"
import { ICON } from "@/lib/icons"

defineProps<{
  rejections: RejectedCapturePhoto[]
}>()

const emit = defineEmits<{
  retry: [clientUploadId: string]
  discard: [clientUploadId: string]
}>()

const confirming = shallowRef<Set<string>>(new Set())

function requestDiscard(clientUploadId: string): void {
  confirming.value = new Set(confirming.value).add(clientUploadId)
}

function keepPhoto(clientUploadId: string): void {
  const next = new Set(confirming.value)
  next.delete(clientUploadId)
  confirming.value = next
}

function confirmDiscard(clientUploadId: string): void {
  keepPhoto(clientUploadId)
  emit("discard", clientUploadId)
}
</script>

<template>
  <div class="pointer-events-auto flex flex-col gap-2 px-4">
    <div
      v-for="rejection in rejections"
      :key="rejection.clientUploadId"
      data-testid="rejected-photo"
      role="alert"
      aria-live="assertive"
      class="border-error bg-error text-inverted flex flex-col gap-2 rounded-xl border px-4 py-3 text-sm"
    >
      <p>{{ rejection.message }}</p>
      <div v-if="!confirming.has(rejection.clientUploadId)" class="grid grid-cols-2 gap-2">
        <UButton
          data-testid="retry-rejected-photo"
          block
          size="lg"
          color="neutral"
          variant="solid"
          :icon="ICON.rotate"
          @click="emit('retry', rejection.clientUploadId)"
        >
          Retry
        </UButton>
        <UButton
          data-testid="discard-rejected-photo"
          block
          size="lg"
          color="neutral"
          variant="outline"
          :icon="ICON.delete"
          @click="requestDiscard(rejection.clientUploadId)"
        >
          Discard
        </UButton>
      </div>
      <div v-else class="flex flex-col gap-2">
        <p class="font-medium">Discard this photo permanently?</p>
        <div class="grid grid-cols-2 gap-2">
          <UButton
            block
            size="lg"
            color="neutral"
            variant="outline"
            @click="keepPhoto(rejection.clientUploadId)"
          >
            Keep photo
          </UButton>
          <UButton
            data-testid="confirm-discard-rejected-photo"
            block
            size="lg"
            color="error"
            @click="confirmDiscard(rejection.clientUploadId)"
          >
            Discard photo
          </UButton>
        </div>
      </div>
    </div>
  </div>
</template>
